import { Injectable, ComponentRef, ViewContainerRef, inject } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { ModalComponent } from '../modal.component';
import { ModalConfig, ModalResult } from '../interfaces/modal.interface';

export interface ModalRef {
  close: (result?: any) => void;
  afterClosed: () => Observable<any>;
  componentInstance: ModalComponent;
}

@Injectable({
  providedIn: 'root'
})
export class ModalService {
  private viewContainerRef?: ViewContainerRef;
  private openModals: Map<string, ModalRef> = new Map();

  setViewContainerRef(viewContainerRef: ViewContainerRef) {
    this.viewContainerRef = viewContainerRef;
  }

  open(config: ModalConfig, data?: any): ModalRef {
    if (!this.viewContainerRef) {
      throw new Error('ViewContainerRef not set. Call setViewContainerRef() first.');
    }

    const componentRef: ComponentRef<ModalComponent> = this.viewContainerRef.createComponent(ModalComponent);
    const modalInstance = componentRef.instance;
    const modalId = this.generateModalId();

    // Configure modal
    modalInstance.config = { ...config };
    modalInstance.isOpen = true;
    modalInstance.customContent = false;

    // Create result subject
    const afterClosedSubject = new Subject<any>();

    // Create modal reference
    const modalRef: ModalRef = {
      close: (result?: any) => {
        modalInstance.isOpen = false;
        setTimeout(() => {
          componentRef.destroy();
          afterClosedSubject.next(result);
          afterClosedSubject.complete();
          this.openModals.delete(modalId);
        }, 300); // Wait for animation
      },
      afterClosed: () => afterClosedSubject.asObservable(),
      componentInstance: modalInstance
    };

    // Handle modal events
    modalInstance.modalClose.subscribe(() => {
      modalRef.close();
    });

    modalInstance.buttonClick.subscribe((action: string) => {
      modalRef.close({ action, data });
    });

    modalInstance.backdropClick.subscribe(() => {
      modalRef.close({ action: 'backdrop' });
    });

    // Store modal reference
    this.openModals.set(modalId, modalRef);

    return modalRef;
  }

  confirm(title: string, message: string, confirmText = 'Confirmar', cancelText = 'Cancelar'): Observable<boolean> {
    const config: ModalConfig = {
      title,
      content: message,
      size: 'md',
      showCloseButton: true,
      closeOnBackdrop: true,
      buttons: [
        {
          text: cancelText,
          type: 'secondary',
          action: 'cancel'
        },
        {
          text: confirmText,
          type: 'primary',
          action: 'confirm'
        }
      ]
    };

    const modalRef = this.open(config);

    return new Observable<boolean>(observer => {
      modalRef.afterClosed().subscribe((result: ModalResult) => {
        observer.next(result?.action === 'confirm');
        observer.complete();
      });
    });
  }

  alert(title: string, message: string, buttonText = 'Aceptar'): Observable<void> {
    const config: ModalConfig = {
      title,
      content: message,
      size: 'md',
      showCloseButton: true,
      closeOnBackdrop: true,
      buttons: [
        {
          text: buttonText,
          type: 'primary',
          action: 'ok'
        }
      ]
    };

    const modalRef = this.open(config);

    return new Observable<void>(observer => {
      modalRef.afterClosed().subscribe(() => {
        observer.next();
        observer.complete();
      });
    });
  }

  closeAll(): void {
    this.openModals.forEach(modalRef => {
      modalRef.close();
    });
  }

  private generateModalId(): string {
    return 'modal_' + Math.random().toString(36).substr(2, 9);
  }
}
